import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import BrandLockup from "@/components/common/BrandLockup";
import Button from "@/components/common/Button";
import { ArrowRight, MenuIcon } from "@/components/common/icons";
import { env } from "@/lib/env";
import { cx } from "@/lib/cx";
import { NAV_ITEMS } from "@/lib/nav";
import { useLocale } from "@/i18n/useLocale";
import { chromeFor } from "@/content/chrome";
import { pathFor } from "@/routes/manifest";
import LanguageSwitcher from "@/components/common/LanguageSwitcher";

export default function Header(): JSX.Element {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const locale = useLocale();
  const chrome = chromeFor(locale);

  // Built from content rather than a module-level constant because the labels
  // are locale-dependent while the destinations are not.
  const mobileLegalLinks = [
    { label: chrome.legal.privacy, href: env.legalPrivacyUrl },
    { label: chrome.legal.termsCompact, href: env.legalTermsUrl },
  ];

  useEffect(() => {
    const onScroll = (): void => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close the mobile menu when the user navigates to a new route. The
  // <NavLink onClick> handlers cover the link-click case; this effect
  // covers programmatic / browser-back navigations.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Close on Escape — mobile users with a connected keyboard, plus a
  // standard accessibility expectation for any open overlay.
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  // Lock body scroll while the menu overlays the page so the user
  // doesn't accidentally scroll the underlying content.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [menuOpen]);

  return (
    <>
      <header className={cx("header", scrolled && "scrolled")}>
        <div className="container-page flex items-center justify-between w-full">
          <BrandLockup />
          <nav className="hidden md:flex items-center gap-1" aria-label={chrome.nav.ariaPrimary}>
            {NAV_ITEMS.map((n) => (
              <NavLink
                key={n.id}
                to={pathFor(n.id, locale)}
                end={n.end}
                className={({ isActive }) => cx("nav-link", isActive && "active")}
              >
                {chrome.nav.labels[n.id]}
              </NavLink>
            ))}
          </nav>
          <div className="flex gap-2 items-center">
            <LanguageSwitcher className="hidden md:flex" />
            <Button variant="secondary" href={env.loginUrl} className="hidden sm:inline-flex">
              {chrome.cta.signIn}
            </Button>
            <Button variant="primary" orbit href={env.registerUrl} className="hidden md:inline-flex">
              {chrome.cta.getFunded} <ArrowRight />
            </Button>
            <button
              type="button"
              className="menu-toggle md:hidden"
              aria-label={menuOpen ? chrome.nav.closeMenu : chrome.nav.openMenu}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <MenuIcon open={menuOpen} />
            </button>
          </div>
        </div>
      </header>

      {/*
       * Mobile-only nav overlay. Always mounted so CSS transitions can
       * animate enter/exit; visibility/pointer-events/aria-hidden are
       * tied to `menuOpen` so the closed state is unfocusable AND
       * excluded from the accessibility tree (the latter is what keeps
       * `getByRole(...)` queries in Header.test.tsx single-match — the
       * mobile NavLinks are filtered out of the a11y tree when closed
       * and the test default is closed). The panel sits below the
       * fixed header (top-header) and fills the remaining viewport.
       */}
      <div
        id="mobile-nav"
        className={cx("mobile-nav md:hidden", menuOpen && "open")}
        aria-hidden={!menuOpen}
      >
        <nav
          className="container-page py-6 flex flex-col gap-1"
          aria-label={chrome.nav.ariaMobile}
        >
          {NAV_ITEMS.map((n) => (
            <NavLink
              key={n.id}
              to={pathFor(n.id, locale)}
              end={n.end}
              tabIndex={menuOpen ? 0 : -1}
              className={({ isActive }) => cx("mobile-nav-link", isActive && "active")}
              onClick={() => setMenuOpen(false)}
            >
              {chrome.nav.labels[n.id]}
            </NavLink>
          ))}
          <Button
            variant="secondary"
            href={env.loginUrl}
            className="mt-5 w-full justify-center sm:hidden"
            tabIndex={menuOpen ? 0 : -1}
          >
            {chrome.cta.signIn}
          </Button>
          <Button
            variant="primary"
            orbit
            href={env.registerUrl}
            className="mt-2 sm:mt-3 w-full justify-center"
            tabIndex={menuOpen ? 0 : -1}
          >
            {chrome.cta.getFunded} <ArrowRight />
          </Button>

          <LanguageSwitcher className="mt-6 justify-start" tabIndex={menuOpen ? 0 : -1} />

          <div className="mt-8 pt-5 border-t border-border flex flex-col gap-1.5" aria-label={chrome.nav.ariaLegal}>
            {mobileLegalLinks.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                tabIndex={menuOpen ? 0 : -1}
                className="text-[12px] text-fg-dim hover:text-fg-muted transition-colors py-0.5"
              >
                {l.label}
              </a>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
}
